/**
 * The shop's own vocabulary.
 *
 * Every product is described with words this screen owns: its colour, its
 * category, its brand, who it came from, and which run of sizes it uses. The
 * product form can only ever offer what is on these lists, so when next season
 * arrives in rust and emerald, this is where they get added - without anyone
 * touching the database.
 *
 * Nothing here can be deleted. A colour that was used on a garment sold last
 * March has to keep existing or that receipt stops making sense; renaming is
 * always the right move instead.
 */
import { useState } from 'react';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { Button, Empty, Field, Modal, Swatch, Tabs } from '../../core/ui/ui.js';
import { useRefs } from './Products.js';

type Tab = 'colors' | 'categories' | 'brands' | 'suppliers' | 'scales';

interface Named {
  id: string;
  name_en: string;
  name_ar: string;
  hex?: string;
  sizes?: string[];
}

export function Lists() {
  const t = useT();
  const refs = useRefs();
  const [tab, setTab] = useState<Tab>('colors');

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'colors', label: t('lists.colours'), count: refs.colors.length },
    { key: 'categories', label: t('lists.categories'), count: refs.categories.length },
    { key: 'brands', label: t('lists.brands'), count: refs.brands.length },
    { key: 'suppliers', label: t('lists.suppliers'), count: refs.suppliers.length },
    { key: 'scales', label: t('lists.sizeScales'), count: refs.sizeScales.length },
  ];

  return (
    <div className="col">
      <div className="card">
        <p className="small muted">{t('lists.intro')}</p>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'colors' ? (
        <NamedList rows={refs.colors} endpoint="colors" withColour onChanged={refs.reload} />
      ) : null}
      {tab === 'categories' ? (
        <NamedList rows={refs.categories} endpoint="categories" onChanged={refs.reload} />
      ) : null}
      {tab === 'brands' ? (
        <NamedList rows={refs.brands} endpoint="brands" onChanged={refs.reload} />
      ) : null}
      {tab === 'suppliers' ? <SupplierList rows={refs.suppliers} onChanged={refs.reload} /> : null}
      {tab === 'scales' ? <ScaleList rows={refs.sizeScales} onChanged={refs.reload} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Colours, categories and brands - a name in each language, and for a colour
// the shade itself so it can be recognised at a glance on the till.
// ---------------------------------------------------------------------------

function NamedList({
  rows,
  endpoint,
  withColour,
  onChanged,
}: {
  rows: Named[];
  endpoint: string;
  withColour?: boolean;
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const { lang } = useI18n();
  const [editing, setEditing] = useState<Named | 'new' | null>(null);
  const [q, setQ] = useState('');

  const nm = (x: Named) => (lang === 'ar' ? x.name_ar || x.name_en : x.name_en || x.name_ar);
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? rows.filter((r) => `${r.name_en} ${r.name_ar}`.toLowerCase().includes(needle))
    : rows;

  return (
    <div className="card">
      <div className="card-head">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('lists.searchPlaceholder')} aria-label={t('lists.searchPlaceholder')}
          style={{ maxWidth: 280 }}
        />
        <div className="spacer" />
        <Button variant="primary" onClick={() => setEditing('new')}>
          {t('lists.addNew')}
        </Button>
      </div>

      {shown.length === 0 ? (
        <Empty icon="📝" title={needle ? t('common.noResults') : t('lists.empty')} hint={needle ? undefined : t('lists.emptyHint')} />
      ) : (
        <div className="recv-list">
          {shown.map((row) => (
            <div key={row.id} className="recv-variant">
              <span className="grow">
                {withColour ? <Swatch hex={row.hex} /> : null}{' '}
                <span className="strong">{nm(row)}</span>{' '}
                <span className="xsmall muted">
                  {lang === 'ar' ? row.name_en : row.name_ar}
                </span>
              </span>
              <Button size="sm" onClick={() => setEditing(row)}>
                {t('common.edit')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <NamedEditor
          row={editing === 'new' ? null : editing}
          endpoint={endpoint}
          withColour={withColour}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function NamedEditor({
  row,
  endpoint,
  withColour,
  onClose,
  onSaved,
}: {
  row: Named | null;
  endpoint: string;
  withColour?: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const toast = useToast();
  const [nameEn, setNameEn] = useState(row?.name_en ?? '');
  const [nameAr, setNameAr] = useState(row?.name_ar ?? '');
  const [hex, setHex] = useState(row?.hex || '#888888');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = { nameEn: nameEn.trim(), nameAr: nameAr.trim() };
      if (withColour) body.hex = hex;
      if (row) await api(`/catalog/${endpoint}/${row.id}`, { method: 'PUT', body });
      else await api(`/catalog/${endpoint}`, { body });
      toast.ok(t('lists.saved'));
      await onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // One of the two names is enough - plenty of brands are only ever written one
  // way - but saving something with no name at all just makes an empty row.
  const ok = Boolean(nameEn.trim() || nameAr.trim());

  return (
    <Modal
      title={row ? t('common.edit') : t('lists.addNew')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={!ok} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="col">
        <Field label={t('lists.nameEn')}>
          <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} dir="ltr" autoFocus />
        </Field>
        <Field label={t('lists.nameAr')}>
          <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />
        </Field>
        {withColour ? (
          <Field label={t('lists.shade')} hint={t('lists.shadeHint')}>
            <div className="row">
              <input
                type="color"
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                style={{ width: 56, minHeight: 'var(--touch)', padding: 2 }}
              />
              <input value={hex} onChange={(e) => setHex(e.target.value)} dir="ltr" style={{ width: 120 }} />
            </div>
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Suppliers - one name, plus a phone number worth having when a delivery is
// short.
// ---------------------------------------------------------------------------

function SupplierList({
  rows,
  onChanged,
}: {
  rows: { id: string; name: string; phone?: string; notes?: string }[];
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const [editing, setEditing] = useState<{ id: string; name: string; phone?: string; notes?: string } | 'new' | null>(
    null,
  );

  return (
    <div className="card">
      <div className="card-head">
        <div className="spacer" />
        <Button variant="primary" onClick={() => setEditing('new')}>
          {t('lists.addNew')}
        </Button>
      </div>

      {rows.length === 0 ? (
        <Empty icon="🚚" title={t('lists.empty')} hint={t('lists.emptyHint')} />
      ) : (
        <div className="recv-list">
          {rows.map((row) => (
            <div key={row.id} className="recv-variant">
              <span className="grow">
                <span className="strong">{row.name}</span>{' '}
                {row.phone ? <span className="xsmall muted" dir="ltr">{row.phone}</span> : null}
              </span>
              <Button size="sm" onClick={() => setEditing(row)}>
                {t('common.edit')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <SupplierEditor
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function SupplierEditor({
  row,
  onClose,
  onSaved,
}: {
  row: { id: string; name: string; phone?: string; notes?: string } | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState(row?.name ?? '');
  const [phone, setPhone] = useState(row?.phone ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: name.trim(), phone: phone.trim(), notes: notes.trim() };
      if (row) await api(`/catalog/suppliers/${row.id}`, { method: 'PUT', body });
      else await api('/catalog/suppliers', { body });
      toast.ok(t('lists.saved'));
      await onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={row ? t('common.edit') : t('lists.addNew')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={!name.trim()} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="col">
        <Field label={t('common.name')}>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('common.phone')}>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" inputMode="tel" />
        </Field>
        <Field label={t('common.notes')}>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Size scales - the run of sizes a kind of garment comes in. Belts go
// 70/75/80/85, shoes go 36 to 46, shirts go XS to XXXL.
// ---------------------------------------------------------------------------

function ScaleList({ rows, onChanged }: { rows: Named[]; onChanged: () => Promise<void> }) {
  const t = useT();
  const { lang } = useI18n();
  const [editing, setEditing] = useState<Named | 'new' | null>(null);

  const nm = (x: Named) => (lang === 'ar' ? x.name_ar || x.name_en : x.name_en || x.name_ar);

  return (
    <div className="card">
      <div className="card-head">
        <div className="spacer" />
        <Button variant="primary" onClick={() => setEditing('new')}>
          {t('lists.addNew')}
        </Button>
      </div>

      {rows.length === 0 ? (
        <Empty icon="📏" title={t('lists.empty')} hint={t('lists.emptyHint')} />
      ) : (
        <div className="recv-list">
          {rows.map((row) => (
            <div key={row.id} className="recv-style">
              <div className="recv-style-head">
                <div className="grow">
                  <div className="strong">{nm(row)}</div>
                  <div className="xsmall muted">{(row.sizes ?? []).join(' · ')}</div>
                </div>
                <Button size="sm" onClick={() => setEditing(row)}>
                  {t('common.edit')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <ScaleEditor
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function ScaleEditor({
  row,
  onClose,
  onSaved,
}: {
  row: Named | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const toast = useToast();
  const [nameEn, setNameEn] = useState(row?.name_en ?? '');
  const [nameAr, setNameAr] = useState(row?.name_ar ?? '');
  const [text, setText] = useState((row?.sizes ?? []).join(', '));
  const [busy, setBusy] = useState(false);

  // Typed as one line because that is how a size run is said out loud.
  // Arabic commas count too - the keyboard gives those, not the Latin one.
  const sizes = text
    .split(/[,،؛\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      await api('/catalog/size-scales', {
        body: {
          // Sending the same id back is how an existing scale is edited.
          ...(row ? { id: row.id } : {}),
          nameEn: nameEn.trim(),
          nameAr: nameAr.trim(),
          kind: 'custom',
          sizes,
        },
      });
      toast.ok(t('lists.saved'));
      await onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ok = Boolean((nameEn.trim() || nameAr.trim()) && sizes.length > 0);

  return (
    <Modal
      title={row ? t('common.edit') : t('lists.addNew')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={!ok} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="col">
        <Field label={t('lists.nameEn')}>
          <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} dir="ltr" autoFocus />
        </Field>
        <Field label={t('lists.nameAr')}>
          <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />
        </Field>
        <Field label={t('lists.theSizes')} hint={t('lists.theSizesHint')}>
          <input value={text} onChange={(e) => setText(e.target.value)} dir="ltr" />
        </Field>
        {sizes.length > 0 ? (
          <div className="btn-row">
            {sizes.map((s, i) => (
              <span key={`${s}-${i}`} className="pill">
                {s}
              </span>
            ))}
          </div>
        ) : null}
        {row ? <p className="xsmall muted">{t('lists.scaleEditNote')}</p> : null}
      </div>
    </Modal>
  );
}
